/* Lab 1.7 · 强化学习：在试错里变强.

   train.py trained a two-link arm with PPO twice, on the same environment: once rewarded for reaching the
   cup and leaving it where it is, once rewarded only for moving the cup. data.json holds both learning
   curves (measured on 100 fixed cup positions) and the policy's weights at 12 points of training. This
   page re-implements the environment and runs those saved policies live: exploration adds the policy's own
   Gaussian noise, scaled by the slider, from a seeded generator.

   The scripted take (8.8 s, one segment of episode 1.7):
     0–5.4   reward "reach": four episodes at iterations 0, 10, 50 and 200 of training: from flailing to a
             clean reach, while the cursor climbs the reward curve
     5.4–8.8 reward "moved", fully trained: it swipes the cup away; its reward is as high as the other one's,
             and the task is almost never done */
import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { C, q, recorded, createStage, floor, run, rng, clamp, span, el } from "../kit/stage";
import { Chart } from "../kit/chart";
import { localizePage, tr, type Dict } from "../kit/i18n";
import DATA from "./data.json";
import STRINGS from "./i18n.json";

localizePage(STRINGS as Dict);

type Kind = "reach" | "moved";
interface Ck { w: number[][][]; b: number[][]; std: number[] }
interface Pt { iter: number; eval_return: number; success: number }
const E = DATA.env;
const runs = DATA.runs as unknown as Record<Kind, { curve: Pt[]; checkpoints: Record<string, Ck> }>;
const ITERS = Object.keys(runs.reach.checkpoints).map(Number).sort((a, b) => a - b);

// --- the environment, exactly as train.py
const fk = (q0: number, q1: number): [number, number] => [E.L1 * Math.cos(q0) + E.L2 * Math.cos(q0 + q1), E.L1 * Math.sin(q0) + E.L2 * Math.sin(q0 + q1)];
interface Ep { q: [number, number]; qd: [number, number]; cup: [number, number]; start: [number, number]; ret: number; k: number;
  hand: [number, number][]; cups: [number, number][] }
function newEp(cup: [number, number]): Ep {
  const [a, b] = E.q0 as number[];
  return { q: [a, b], qd: [0, 0], cup: [...cup], start: [...cup], ret: 0, k: 0, hand: [fk(a, b)], cups: [[...cup]] };
}
function policy(ck: Ck, o: number[]): number[] {
  let x = o;
  ck.w.forEach((W, l) => {
    x = W.map((row, i) => row.reduce((s, w, j) => s + w * x[j], ck.b[l][i]));
    if (l < ck.w.length - 1) x = x.map(Math.tanh);
  });
  return x;
}
function stepEp(ep: Ep, ck: Ck, kind: Kind, noise: number, rand: () => number): void {
  const h = fk(...ep.q);
  const o = [Math.sin(ep.q[0]), Math.sin(ep.q[1]), Math.cos(ep.q[0]), Math.cos(ep.q[1]), ep.qd[0] / E.vmax, ep.qd[1] / E.vmax,
    (ep.cup[0] - h[0]) * 3, (ep.cup[1] - h[1]) * 3, ep.cup[0] * 3, ep.cup[1] * 3];
  const mu = policy(ck, o);
  const a = mu.map((m, i) => {   // Box–Muller with the seeded generator
    const g = Math.sqrt(-2 * Math.log(Math.max(1e-9, rand()))) * Math.cos(2 * Math.PI * rand());
    return m + noise * ck.std[i] * g;
  });
  ep.qd = [clamp(a[0], -1, 1) * E.vmax, clamp(a[1], -1, 1) * E.vmax];
  ep.q = [ep.q[0] + ep.qd[0] * E.dt, ep.q[1] + ep.qd[1] * E.dt];
  const nh = fk(...ep.q), dx = ep.cup[0] - nh[0], dy = ep.cup[1] - nh[1], d = Math.hypot(dx, dy);
  let moved = 0;
  if (d < E.cup_r) {
    const k = (E.cup_r - d) / Math.max(d, 1e-6);
    ep.cup = [ep.cup[0] + dx * k, ep.cup[1] + dy * k];
    moved = E.cup_r - d;
  }
  const dist = Math.hypot(ep.cup[0] - nh[0], ep.cup[1] - nh[1]), speed = Math.hypot(...ep.qd) * .5;
  ep.ret += kind === "moved" ? 20 * moved
    : -Math.max(0, dist - .05) - .01 * (clamp(a[0], -1e9, 1e9) ** 2 + a[1] ** 2) - 20 * moved + (dist < .06 && speed < .2 ? .5 : 0);
  ep.k++;
  ep.hand.push(nh);
  ep.cups.push([...ep.cup]);
}
const done = (ep: Ep): boolean => { const h = fk(...ep.q); return Math.hypot(ep.cup[0] - h[0], ep.cup[1] - h[1]) < .06 && Math.hypot(ep.cup[0] - ep.start[0], ep.cup[1] - ep.start[1]) < .03; };

// --- the scripted take: a list of episodes, each shown for a stretch of the take
const CUPS: [number, number][] = [[.34, .12], [.30, -.18], [.40, .06], [.28, .2], [.36, -.08], [.33, .15]];
interface Beat { from: number; to: number; kind: Kind; ck: number; cup: [number, number]; seed: number; noise: number }
const TAKE: Beat[] = [
  // early on, with the exploration noise it trains with; later, as evaluated (the policy's mean action, no noise)
  { from: 0, to: 1.35, kind: "reach", ck: 0, cup: CUPS[0], seed: 1, noise: 1 },
  { from: 1.35, to: 2.7, kind: "reach", ck: 3, cup: CUPS[1], seed: 2, noise: 1 },
  { from: 2.7, to: 4.05, kind: "reach", ck: 6, cup: CUPS[2], seed: 3, noise: 0 },
  { from: 4.05, to: 5.4, kind: "reach", ck: 11, cup: CUPS[3], seed: 4, noise: 0 },
  { from: 5.4, to: 7.1, kind: "moved", ck: 11, cup: CUPS[4], seed: 5, noise: 0 },
  { from: 7.1, to: 8.8, kind: "moved", ck: 11, cup: CUPS[5], seed: 6, noise: 0 },
];
const simulate = (b: Beat, steps: number): Ep => {
  const ep = newEp(b.cup), rand = rng(b.seed), ck = runs[b.kind].checkpoints[String(ITERS[b.ck])];
  for (let i = 0; i < Math.min(steps, E.steps); i++) stepEp(ep, ck, b.kind, b.noise, rand);
  return ep;
};

// --- scene
const canvas = el<HTMLCanvasElement>("stage");
const { scene, camera, render } = createStage(canvas);
camera.setViewOffset(1920, 1080, -140, -170, 1920, 1080);   // the arm sits between the two panels, a little low
floor(scene);
const S = (x: number, y: number, h = 0): THREE.Vector3 => new THREE.Vector3(x, h, -y);   // env (x forward, y left) to scene
const metal = (color: number, m = .7, r = .4) => new THREE.MeshStandardMaterial({ color, metalness: m, roughness: r });
const base = new THREE.Mesh(new THREE.CylinderGeometry(.05, .06, .04, 40), metal(0x2c3444));
base.position.y = .02;
scene.add(base);
const link = (len: number, mat: THREE.Material): THREE.Group => {
  const g = new THREE.Group();
  const m = new THREE.Mesh(new THREE.CapsuleGeometry(.016, len, 8, 20), mat);
  m.rotation.z = -Math.PI / 2;
  m.position.x = len / 2;
  m.castShadow = true;
  g.add(m);
  const j = new THREE.Mesh(new THREE.CylinderGeometry(.026, .026, .04, 32), metal(0x566274, .85, .3));
  g.add(j);
  return g;
};
const armMat = metal(0x9aa8bd, .8, .3);
const l1 = link(E.L1, armMat), l2 = link(E.L2, armMat);
l1.position.y = .06;
l2.position.x = E.L1;
l1.add(l2);
scene.add(l1);
const handMat = new THREE.MeshStandardMaterial({ color: C.act, emissive: C.act, emissiveIntensity: 1.4 });
const hand = new THREE.Mesh(new THREE.SphereGeometry(.022, 24, 16), handMat);
hand.position.x = E.L2;
l2.add(hand);
const cupMat = new THREE.MeshStandardMaterial({ color: 0x2a5b8a, metalness: .2, roughness: .35 });
const cup = new THREE.Mesh(new THREE.CylinderGeometry(E.cup_r, E.cup_r * .92, .08, 40), cupMat);
cup.castShadow = true;
scene.add(cup);
const startRing = new THREE.Mesh(new THREE.RingGeometry(E.cup_r + .004, E.cup_r + .012, 40), new THREE.MeshBasicMaterial({ color: C.see, side: THREE.DoubleSide, transparent: true, opacity: .8 }));
startRing.rotation.x = -Math.PI / 2;
scene.add(startRing);
const trailGeo = new THREE.BufferGeometry(), trailMat = new THREE.LineBasicMaterial({ color: C.act, transparent: true, opacity: .8 });
scene.add(new THREE.Line(trailGeo, trailMat));

const controls = new OrbitControls(camera, canvas);
controls.target.set(.2, .03, 0);
Object.assign(controls, { enableDamping: true, minDistance: .4, maxDistance: 2.5, maxPolarAngle: 1.45, enabled: !recorded });
camera.position.set(.75, 1.25, -.99);

// --- charts
const x = [0, ITERS[ITERS.length - 1]] as [number, number];
const ret = new Chart(el("c1"), { x, y: [-8, 26], yTicks: [0, 10, 20], xTicks: [0, 50, 100, 150, 200] }, 700, 170);
const suc = new Chart(el("c2"), { x, y: [0, 1], yTicks: [0, .5, 1], xTicks: [0, 50, 100, 150, 200], yFormat: v => `${Math.round(v * 100)}%`, xFormat: v => tr("第 {0} 轮", v) }, 700, 150);
const COL: Record<Kind, string> = { reach: "#34d399", moved: "#fbbf24" };

// --- live state
const user = { kind: "reach" as Kind, ck: 0, noise: 1, seed: 1 };
let live: { ep: Ep; beat: Beat; t0: number } | null = null;
const ckIn = el<HTMLInputElement>("ck"), noiseIn = el<HTMLInputElement>("noise");
ckIn.addEventListener("input", () => { user.ck = Number(ckIn.value); live = null; });
noiseIn.addEventListener("input", () => { user.noise = Number(noiseIn.value); live = null; });
el("kind").addEventListener("click", e => { const k = (e.target as HTMLElement).dataset.k as Kind | undefined; if (k) { user.kind = k; live = null; } });

function frameAt(t: number): void {
  let ep: Ep, beat: Beat, noise = 1;
  if (recorded) {
    beat = TAKE.find(b => t < b.to) ?? TAKE[TAKE.length - 1];
    const k = Math.floor(span(t, beat.from, beat.to - .15) * E.steps);
    ep = simulate(beat, k);
    noise = beat.noise;
  } else {
    const r = rng(user.seed * 7919);
    if (!live || (t - live.t0) > E.steps * E.dt + .8) {
      user.seed++;
      const rr = .22 + .26 * r(), th = (r() * 2 - 1) * Math.PI / 3;
      const b: Beat = { from: 0, to: 0, kind: user.kind, ck: user.ck, cup: [rr * Math.cos(th), rr * Math.sin(th)], seed: user.seed, noise: user.noise };
      live = { ep: newEp(b.cup), beat: b, t0: t };
    }
    beat = live.beat;
    noise = user.noise;
    const want = Math.min(E.steps, Math.floor((t - live.t0) / E.dt));
    const ck = runs[beat.kind].checkpoints[String(ITERS[beat.ck])], rand = rng(beat.seed);
    live.ep = newEp(beat.cup);
    for (let i = 0; i < want; i++) stepEp(live.ep, ck, beat.kind, noise, rand);
    ep = live.ep;
    el("ckVal").textContent = tr("第 {0} 轮", ITERS[user.ck]);
    el("noiseVal").textContent = `× ${user.noise.toFixed(2)}`;
  }

  // arm, cup, trail
  l1.rotation.y = ep.q[0];
  l2.rotation.y = ep.q[1];
  cup.position.copy(S(ep.cup[0], ep.cup[1], .04));
  startRing.position.copy(S(ep.start[0], ep.start[1], .002));
  const pushed = Math.hypot(ep.cup[0] - ep.start[0], ep.cup[1] - ep.start[1]) >= .03, ok = done(ep);
  trailMat.color.set(beat.kind === "moved" || pushed ? C.amber : ok ? C.see : C.act);
  trailGeo.setFromPoints(ep.hand.map(([hx, hy]) => S(hx, hy, .06)));
  cupMat.color.set(pushed ? 0x8a5a1a : 0x2a5b8a);

  // curves: both runs, the current one bright, a cursor at this checkpoint
  const iter = ITERS[beat.ck];
  const series = (key: "eval_return" | "success") => (["reach", "moved"] as Kind[]).map(k => ({
    points: runs[k].curve.map(p => [p.iter, p[key]] as [number, number]), colour: COL[k], width: k === beat.kind ? 2.6 : 1.4, opacity: k === beat.kind ? 1 : .35 }));
  ret.draw({ series: series("eval_return"), cursor: iter });
  suc.draw({ series: series("success"), cursor: iter });

  const p = runs[beat.kind].curve.find(c => c.iter === iter)!;
  document.querySelectorAll<HTMLButtonElement>("#kind button").forEach(b => b.classList.toggle("on", b.dataset.k === beat.kind));
  el("rows").innerHTML = tr("训练 {0} / {1} · 探索噪声 σ {2}", `<b>${tr("第 {0} 轮", iter)}</b>`, ITERS[ITERS.length - 1],
      `<b>${(runs[beat.kind].checkpoints[String(iter)].std[0] * noise).toFixed(2)}</b>`) + (noise ? "" : tr("（按评测方式：不加噪声）")) + "<br>" +
    tr("这一轮评测：平均回报 {0} · 做成 {1}", `<b>${p.eval_return.toFixed(1)}</b>`, `<b>${Math.round(p.success * 100)}%</b>`) + "<br>" +
    tr("这一局：第 {0} s · 得分 {1}", `<b>${(ep.k * E.dt).toFixed(1)}</b>`, `<b>${ep.ret.toFixed(1)}</b>`);
  el("reward").innerHTML = beat.kind === "reach"
    ? tr("奖励：<code>− 离杯子的距离 − 推动杯子 + 停在杯子旁</code>")
    : tr("奖励：<code>+ 杯子移动的距离</code>，别的都不管");
  const head = (h: string, d: string): string => `${tr(h)}<small>${tr(d)}</small>`;
  const v = el("verdict");
  const finished = ep.k >= E.steps || (recorded && t > beat.to - .3);
  if (beat.kind === "moved" && pushed) { v.className = "verdict warn"; v.innerHTML = head("! 钻空子", "把杯子扫飞，分数照涨，任务没做成"); }
  else if (ok && finished) { v.className = "verdict ok"; v.innerHTML = head("✓ 够到了", "停在杯子旁，杯子没被推开"); }
  else if (finished) { v.className = "verdict warn"; v.innerHTML = pushed ? head("! 把杯子推开了", "这一局没做成") : head("! 没够到", "这一局没做成"); }
  else { v.className = "verdict busy"; v.innerHTML = iter < 10 ? head("▸ 乱试", "还没学会，动作几乎是随机的") : head("▸ 伸手中", "按学到的策略出动作"); }
  el("src").textContent = tr("PPO · {0} 个并行环境 × {1} 轮 · 策略 {2} · 本页在浏览器里重放保存下来的 12 个检查点（train.py，torch {3}）", DATA.algo.envs, DATA.algo.iters, DATA.algo.policy, DATA.torch);

  if (recorded) {
    const az = -1.05 + t * .015, o = controls.target.set(.18, .03, 0);
    camera.position.set(o.x + Math.cos(az) * 1.15, 1.25, o.z + Math.sin(az) * 1.15);
    camera.lookAt(o);
  } else controls.update();
  render();
  el("clock").textContent = "T+" + t.toFixed(2).padStart(5, "0");
  el("dot").style.opacity = (t * 2 | 0) % 2 ? ".3" : "1";
}

run(frameAt, { dur: Number(q.get("dur") || 8.8), still: 4.9 });
